import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Users, Euro, Smartphone, GraduationCap, TrendingUp } from "lucide-react";

const fmtDate = (s) => {
  const [, m, d] = s.split("-");
  return `${d}.${m}`;
};

function StatCard({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="bg-graphite border border-ink/5 p-6" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3 text-ink-muted">
        <Icon size={16} strokeWidth={1.5} className="text-gold" />
        <span className="text-xs tracking-widest uppercase">{label}</span>
      </div>
      <div className="font-serif text-4xl text-ink">{value}</div>
      {sub && <div className="text-ink-muted text-sm mt-1">{sub}</div>}
    </div>
  );
}

export default function Admin() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (user && user.role === "admin") {
      api.get(`/admin/stats?lang=${lang}`).then((r) => setStats(r.data)).catch(() => setError(true));
    }
  }, [user, lang]);

  if (user === null) return <div className="min-h-screen bg-void" />;
  if (!user || user.role !== "admin") return <Navigate to="/dashboard" replace />;

  const maxModule = stats ? Math.max(1, ...stats.modules.map((m) => m.users_started)) : 1;

  return (
    <div className="min-h-screen bg-void">
      <Navbar />
      <main className="pt-28 pb-16 max-w-7xl mx-auto px-6 md:px-12" data-testid="admin-page">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <p className="text-gold text-sm tracking-[0.3em] uppercase mb-3">{t("admin.eyebrow")}</p>
          <h1 className="font-serif text-5xl md:text-6xl text-ink mb-12">{t("admin.title")}</h1>
        </motion.div>

        {error && <p className="text-ink-soft" data-testid="admin-error">{t("admin.error")}</p>}

        {!stats && !error && <p className="text-ink-soft">{t("common.loading")}</p>}

        {stats && (
          <div className="space-y-16">
            {/* Key metrics */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6" data-testid="admin-cards">
              <StatCard icon={Users} label={t("admin.users")} value={stats.users.total}
                sub={`${stats.users.google} Google · ${stats.users.password} E-Mail`} testid="admin-card-users" />
              <StatCard icon={Euro} label={t("admin.revenue")} value={`${stats.revenue.amount_eur.toLocaleString("de-DE", { minimumFractionDigits: 2 })} €`}
                sub={`${stats.revenue.paid_certificates} ${t("admin.certificates")}`} testid="admin-card-revenue" />
              <StatCard icon={Smartphone} label={t("admin.pwa")} value={stats.pwa.installs_total}
                sub={t("admin.pwa_sub")} testid="admin-card-pwa" />
              <StatCard icon={GraduationCap} label={t("admin.completed")} value={stats.progress.completed_course}
                sub={`${stats.progress.in_progress} ${t("admin.in_progress")}`} testid="admin-card-completed" />
            </div>

            {/* Traffic chart */}
            <div>
              <h2 className="font-serif text-3xl text-ink mb-6 flex items-center gap-3">
                <TrendingUp className="text-gold" strokeWidth={1.5} /> {t("admin.traffic")}
              </h2>
              <div className="bg-graphite border border-ink/5 p-4 md:p-6" data-testid="admin-traffic-chart">
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={stats.traffic} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id="gVisits" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#D4AF37" stopOpacity={0.5} />
                        <stop offset="100%" stopColor="#D4AF37" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="gReg" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#8AA0FF" stopOpacity={0.5} />
                        <stop offset="100%" stopColor="#8AA0FF" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(240,235,224,0.06)" />
                    <XAxis dataKey="date" tickFormatter={fmtDate} stroke="#737373" fontSize={11} tickLine={false} />
                    <YAxis stroke="#737373" fontSize={11} tickLine={false} allowDecimals={false} />
                    <Tooltip
                      contentStyle={{ background: "#0a0a0a", border: "1px solid rgba(212,175,55,0.3)", borderRadius: 0, color: "#F0EBE0" }}
                      labelFormatter={fmtDate} />
                    <Area type="monotone" dataKey="visits" name={t("admin.visits")} stroke="#D4AF37" strokeWidth={2} fill="url(#gVisits)" />
                    <Area type="monotone" dataKey="registrations" name={t("admin.registrations")} stroke="#8AA0FF" strokeWidth={2} fill="url(#gReg)" />
                  </AreaChart>
                </ResponsiveContainer>
                <div className="flex flex-wrap gap-6 mt-4 text-sm">
                  <span className="flex items-center gap-2 text-ink-soft"><span className="w-3 h-3 bg-gold" /> {t("admin.visits")}</span>
                  <span className="flex items-center gap-2 text-ink-soft"><span className="w-3 h-3" style={{ background: "#8AA0FF" }} /> {t("admin.registrations")}</span>
                </div>
              </div>
              <p className="text-ink-muted text-sm mt-3">{t("admin.traffic_note")}</p>
            </div>

            {/* Traffic sources */}
            <div>
              <h2 className="font-serif text-3xl text-ink mb-6">{t("admin.traffic_sources")}</h2>
              <div className="bg-graphite border border-ink/5 p-6" data-testid="admin-traffic-sources">
                <div className="flex items-baseline justify-between mb-5 pb-4 border-b border-ink/5">
                  <span className="text-ink-muted text-sm tracking-widest uppercase">{t("admin.total_visitors")}</span>
                  <span className="font-serif text-3xl text-ink">{stats.traffic_sources.total}</span>
                </div>
                {stats.traffic_sources.items.length === 0 ? (
                  <p className="text-ink-muted text-sm">—</p>
                ) : (
                  <div className="space-y-3">
                    {stats.traffic_sources.items.map((s) => (
                      <div key={s.source} className="flex items-center gap-4" data-testid={`admin-source-${s.source}`}>
                        <span className="text-ink w-40 shrink-0 truncate">{s.source}</span>
                        <div className="flex-1 h-1.5 bg-ink/10">
                          <div className="h-full bg-gold" style={{ width: `${s.percent}%` }} />
                        </div>
                        <span className="text-ink-soft text-sm w-24 text-right shrink-0">{s.count} · {s.percent}%</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Registered users */}
            <div>
              <h2 className="font-serif text-3xl text-ink mb-6">{t("admin.registered_users")}</h2>
              <div className="bg-graphite border border-ink/5 divide-y divide-ink/5" data-testid="admin-users-list">
                {stats.recent_users.map((u, i) => (
                  <div key={i} className="p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2" data-testid="admin-user-row">
                    <div className="min-w-0">
                      <div className="text-ink truncate">{u.email}</div>
                      <div className="text-ink-muted text-xs">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString(lang === "de" ? "de-DE" : lang === "ro" ? "ro-RO" : "en-GB") : "—"}
                      </div>
                    </div>
                    <div className="text-sm shrink-0 sm:text-right">
                      <span className={u.source ? "text-gold" : "text-ink-muted"}>
                        {u.source || t("admin.unknown_existing")}
                      </span>
                      {u.campaign && <span className="text-ink-soft"> · {t("admin.campaign")}: {u.campaign}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Module progress */}
            <div>
              <h2 className="font-serif text-3xl text-ink mb-6">{t("admin.modules")}</h2>
              <div className="bg-graphite border border-ink/5 divide-y divide-ink/5" data-testid="admin-modules">
                {stats.modules.map((m, i) => (
                  <div key={m.module_id} className="p-5 flex items-center gap-4" data-testid={`admin-module-${m.module_id}`}>
                    <span className="font-mono text-xs text-ink-muted w-6">{String(i + 1).padStart(2, "0")}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-baseline mb-2 gap-4">
                        <span className="font-serif text-lg text-ink truncate">{m.title}</span>
                        <span className="text-ink-muted text-sm shrink-0">
                          {m.users_started} {t("admin.started")} · {m.users_completed} {t("admin.done")}
                        </span>
                      </div>
                      <div className="h-1 bg-ink/10">
                        <div className="h-full bg-gold transition-all" style={{ width: `${(m.users_started / maxModule) * 100}%` }} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
